/**
 * utils/textFormatter.js
 * ASCII text layout engine for dot matrix printing.
 * Produces monospace 80-column output for use in <pre> blocks.
 *
 * Pure functions — no React, no DOM, no side effects.
 * Every output line is padded/trimmed to exactly LINE_WIDTH (80) chars.
 *
 * Exports:
 *   formatInvoice(transactions, settings) → string
 *   formatSuratJalan(transaction, settings) → string
 */
import { fmtDate } from "./idGenerators";

const LINE_WIDTH = 80;

// ─── Layout helpers ──────────────────────────────────────────────────────────

/** Pad/truncate to exact width, left-aligned */
const padRight = (str, width) => {
  const s = String(str ?? "");
  return s.length >= width ? s.slice(0, width) : s + " ".repeat(width - s.length);
};

/** Pad/truncate to exact width, right-aligned */
const padLeft = (str, width) => {
  const s = String(str ?? "");
  return s.length >= width ? s.slice(0, width) : " ".repeat(width - s.length) + s;
};

/** Center text within a given width, padded with spaces to exact width */
const centerText = (str, width = LINE_WIDTH) => {
  const s = String(str ?? "").slice(0, width);
  const pad = Math.floor((width - s.length) / 2);
  return " ".repeat(pad) + s + " ".repeat(width - pad - s.length);
};

/**
 * Word-wrap text to fit within a given character width.
 * Splits at word boundaries (spaces). Hard-truncates a single word
 * that alone exceeds the full width. Returns array of strings, each <= width chars.
 */
const wrapText = (str, width) => {
  const s = String(str ?? "").trim();
  if (s.length <= width) return [s];
  const words = s.split(/\s+/);
  const lines = [];
  let currentLine = "";
  for (const word of words) {
    if (currentLine === "") {
      if (word.length > width) {
        let remaining = word;
        while (remaining.length > width) {
          lines.push(remaining.slice(0, width));
          remaining = remaining.slice(width);
        }
        currentLine = remaining;
      } else {
        currentLine = word;
      }
    } else if (currentLine.length + 1 + word.length <= width) {
      currentLine += " " + word;
    } else {
      lines.push(currentLine);
      if (word.length > width) {
        let remaining = word;
        while (remaining.length > width) {
          lines.push(remaining.slice(0, width));
          remaining = remaining.slice(width);
        }
        currentLine = remaining;
      } else {
        currentLine = word;
      }
    }
  }
  if (currentLine) lines.push(currentLine);
  return lines;
};

/** Format number without Rp prefix: 5215000 → "5.215.000" */
const fmtNum = (n) => Math.round(Number(n) || 0).toLocaleString("id-ID");

/** Format number with Rp prefix: 5215000 → "Rp 5.215.000" */
const fmtRp = (n) => "Rp " + fmtNum(n);

const SEP_MAJOR = "=".repeat(LINE_WIDTH);
const SEP_MINOR = "_".repeat(LINE_WIDTH);

// ─── Item array helper ───────────────────────────────────────────────────────

/**
 * Returns items array from a transaction, falling back to legacy single-item fields.
 * Matches the same fallback logic used in InvoiceModal.js.
 */
const getItemsArray = (t) => {
  if (Array.isArray(t.items) && t.items.length > 0) return t.items;
  return [{
    itemName:   t.itemName   || "—",
    sackQty:    t.sackQty    != null ? t.sackQty : (t.stockQty || 0),
    weightKg:   t.weightKg   || 0,
    pricePerKg: t.pricePerKg || 0,
    subtotal:   t.value      || 0,
  }];
};

// ─── Invoice sub-formatters ──────────────────────────────────────────────────

/**
 * Invoice header: "I N V O I C E" centered full 80 columns.
 * No business name, address, phone, or page number.
 */
const formatInvoiceHeader = () => {
  return [centerText("I N V O I C E")];
};

/**
 * Invoice meta: two-column layout (left 40 / right 40), matching surat jalan style.
 * Row 1: "KEPADA YTH :" left + "TANGGAL : [date]" right.
 * Row 2: client name left + "NO. INVOICE : [txnId]" right.
 * Row 3+: address lines left (wrapped to 40) paired with CATATAN lines right (only if note non-empty).
 *   CATATAN label (10 chars) on first note line; continuation lines indented to align with text.
 *   Right column blank when note is empty — CATATAN label not rendered at all.
 * Minor separator after meta.
 *
 * @param {Object[]} transactions
 * @param {Object[]} contacts
 * @param {string}   note — session-only invoice note (from DotMatrixPrintModal)
 */
const formatInvoiceMeta = (transactions, contacts = [], note = "") => {
  const t0      = transactions[0] || {};
  const invNo   = t0.txnId        || "—";
  const dateStr = fmtDate(t0.date) || "—";
  const client  = t0.counterparty  || "—";

  const tglStr = "TANGGAL : "     + dateStr;
  const invStr = "NO. INVOICE : " + invNo;

  // Look up client address
  const clientAddr = contacts.find(
    (c) => (c.name || "").toLowerCase() === client.toLowerCase()
  )?.address?.trim();

  const lines = [
    padRight("KEPADA YTH :", 40) + padLeft(tglStr, 40),  // row 1
    padRight(client, 40)          + padLeft(invStr, 40),  // row 2
  ];

  // Build CATATAN lines — only when note is non-empty
  // Label "CATATAN : " = 10 chars; right col = 40 chars; note text width = 30
  const CATATAN_LABEL = "CATATAN : ";
  const CATATAN_TEXT_WIDTH = 40 - CATATAN_LABEL.length; // 30
  const trimmedNote = note.trim();
  const noteLines = trimmedNote
    ? trimmedNote.split(/\r?\n/).flatMap((line) =>
        line.trim() === "" ? [""] : wrapText(line, CATATAN_TEXT_WIDTH)
      )
    : [];

  const addrLines   = clientAddr ? wrapText(clientAddr, 40) : [];
  const NOTE_INDENT = " ".repeat(CATATAN_LABEL.length); // 10 spaces for continuation alignment
  const maxRows     = Math.max(addrLines.length, noteLines.length, 1);

  for (let i = 0; i < maxRows; i++) {
    const leftPart = padRight(addrLines[i] || "", 40);
    let rightPart;
    if (i === 0 && noteLines.length > 0) {
      rightPart = padLeft(CATATAN_LABEL + (noteLines[0] || ""), 40);
    } else if (i > 0 && noteLines[i] !== undefined) {
      rightPart = padLeft(NOTE_INDENT + noteLines[i], 40);
    } else {
      rightPart = " ".repeat(40);
    }
    lines.push(leftPart + rightPart);
  }

  lines.push(SEP_MINOR);
  return lines;
};

/**
 * Items table — 6 columns, with KARUNG column.
 * Columns (total 80): No(6) + Jenis Barang(20) + KARUNG(10) + Berat(13) + Harga(15) + Total(16)
 * No trailing separator — formatInvoiceFooter adds SEP_MAJOR after total row.
 * Flattens all items across all transactions.
 */
const formatItemsTable = (transactions) => {
  const COL_NO     = 6;
  const COL_BARANG = 20;
  const COL_KARUNG = 10;
  const COL_BERAT  = 13;
  const COL_HARGA  = 15;
  const COL_TOTAL  = 16;
  // 6 + 20 + 10 + 13 + 15 + 16 = 80

  const mkRow = (no, barang, karung, berat, harga, total) =>
    padRight(no,     COL_NO)     +
    padRight(barang, COL_BARANG) +
    padLeft(karung,  COL_KARUNG) +
    padLeft(berat,   COL_BERAT)  +
    padLeft(harga,   COL_HARGA)  +
    padLeft(total,   COL_TOTAL);

  const header  = mkRow("No", "Jenis Barang", "KARUNG", "Berat (Kg)", "Harga", "Total");
  const divider = SEP_MINOR;

  // Flatten all items across all transactions
  const lineItems = transactions.flatMap((t) =>
    getItemsArray(t).map((it) => ({ ...it, txType: t.type }))
  );

  const rows = lineItems.flatMap((it, i) => {
    const karungStr = it.sackQty != null && it.sackQty !== 0
      ? fmtNum(it.sackQty) + " SACK"
      : "—";
    const beratStr = it.weightKg   ? fmtNum(it.weightKg)   + " kg" : "—";
    const hargaStr = it.pricePerKg ? "Rp " + fmtNum(it.pricePerKg) : "—";
    const totalStr = "Rp " + fmtNum(it.subtotal || 0);
    const nameLines = wrapText(it.itemName || "—", COL_BARANG);
    const firstRow = mkRow(String(i + 1), nameLines[0], karungStr, beratStr, hargaStr, totalStr);
    const contRows = nameLines.slice(1).map((line) => mkRow("", line, "", "", "", ""));
    return [firstRow, ...contRows];
  });

  return [header, divider, ...rows];
};

/**
 * Invoice footer: total row + major separator + bank accounts + signature block.
 * No LUNAS/SISA TAGIHAN — removed per new format.
 * No notes — note is now shown in formatInvoiceMeta.
 */
const formatInvoiceFooter = (transactions, settings) => {
  const total = transactions.reduce((a, t) => a + (Number(t.value) || 0), 0);
  const lines = [];

  // Major separator, then TOTAL row right-aligned below it
  lines.push(SEP_MAJOR);
  lines.push(padLeft("TOTAL : " + fmtRp(total), LINE_WIDTH));

  // Bank accounts
  const bankAccounts = (settings.bankAccounts || []).filter((a) => a.showOnInvoice);
  const maxShow = settings.maxBankAccountsOnInvoice ?? 1;
  const shown   = maxShow > 0 ? bankAccounts.slice(0, maxShow) : [];

  if (shown.length > 0) {
    shown.forEach((acct, idx) => {
      if (idx > 0) lines.push("");
      if (acct.bankName)      lines.push(padRight("NAME BANK        : " + acct.bankName,      LINE_WIDTH));
      if (acct.accountNumber) lines.push(padRight("ACCOUNT NUMBER   : " + acct.accountNumber, LINE_WIDTH));
      if (acct.accountName)   lines.push(padRight("ACCOUNT NAME     : " + acct.accountName,   LINE_WIDTH));
    });
    lines.push("");
  } else {
    lines.push("");
  }

  // Signature block
  lines.push(
    padRight("TANDA TERIMA,", 40) + padLeft("HORMAT KAMI,", 40),
    "",
    "",
  );

  return lines;
};

// ─── Surat Jalan sub-formatters ──────────────────────────────────────────────

/**
 * Surat jalan header block.
 * Shows only "SURAT JALAN" centered — no company name.
 */
const formatSuratJalanHeader = () => {
  return [centerText("SURAT JALAN"), SEP_MAJOR];
};

/**
 * Surat jalan meta: two-column layout.
 * Row 1: "KEPADA YTH :" (left) + "TANGGAL : [date]" (right)
 * Row 2: client name full-width (no label)
 * Row 3: client address left-half + "PLAT MOBIL : [plat]" right-half (same line)
 *         Continuation address lines appear full-width below.
 * contacts[] is used to look up the recipient's address.
 */
const formatSuratJalanMeta = (transaction, platNomor = "", contacts = []) => {
  const tglStr   = "TANGGAL : " + (fmtDate(transaction.date) || "—");
  const clientNm = transaction.counterparty || "—";
  const platStr  = "PLAT MOBIL : " + platNomor.trim();
  const invStr   = "NO. INVOICE : " + (transaction.txnId || "—");

  // Look up client address
  const clientAddr = contacts.find(
    (c) => (c.name || "").toLowerCase() === clientNm.toLowerCase()
  )?.address?.trim();

  const lines = [
    padRight("KEPADA YTH :", 40) + padLeft(tglStr, 40),  // row 1
    padRight(clientNm, 40)       + padLeft(invStr, 40),   // row 2
  ];

  if (clientAddr) {
    const addrLines = wrapText(clientAddr, 40);
    lines.push(padRight(addrLines[0] || "", 40) + padLeft(platStr, 40));
    for (let i = 1; i < addrLines.length; i++) {
      lines.push(padRight(addrLines[i], LINE_WIDTH));
    }
  } else {
    lines.push(padRight("", 40) + padLeft(platStr, 40));
  }

  lines.push(SEP_MINOR);
  return lines;
};

/**
 * Surat jalan items table — 3-column layout.
 * Columns (total 80): NO.(3) | JENIS BARANG(50) | JUMLAH BARANG(21)
 * Separators: " | " (3 chars) × 2 = 6 chars → 3 + 6 + 50 + 21 = 80
 */
const formatSuratJalanItems = (transaction) => {
  const COL_NO     = 3;
  const COL_BARANG = 50;
  const COL_JML    = 21;
  const SEP        = " | ";

  const mkRow = (no, barang, jml) =>
    padLeft(no, COL_NO) +
    SEP + padRight(barang, COL_BARANG) +
    SEP + padRight(jml, COL_JML);

  const header  = mkRow("NO.", "JENIS BARANG", "JUMLAH BARANG");
  const divider = SEP_MINOR;

  const unit = "SACK";
  const items = getItemsArray(transaction).flatMap((it, i) => {
    const qty = it.sackQty != null ? it.sackQty : 0;
    const jmlStr = fmtNum(qty) + " " + unit;
    const nameLines = wrapText(it.itemName || "—", COL_BARANG);
    const firstRow = mkRow(String(i + 1), nameLines[0], jmlStr);
    const contRows = nameLines.slice(1).map(line => mkRow("", line, ""));
    return [firstRow, ...contRows];
  });

  return [header, divider, ...items, SEP_MAJOR];
};

/**
 * Surat jalan footer: signature blocks for Tanda Terima and Hormat Kami.
 * Each side gets 2 blank lines for signature space.
 */
const formatSuratJalanFooter = (catatanPengiriman = "") => {
  const leftLabel  = padRight("TANDA TERIMA,", 40);
  const rightLabel = padLeft("HORMAT KAMI,", 40);
  const blankLine  = " ".repeat(LINE_WIDTH);

  const lines = [];

  if (catatanPengiriman.trim()) {
    const prefix = "Catatan: ";
    const firstLineWidth = LINE_WIDTH - prefix.length;
    const noteLines = wrapText(catatanPengiriman.trim(), firstLineWidth);
    lines.push(padRight(prefix + (noteLines[0] || ""), LINE_WIDTH));
    for (let i = 1; i < noteLines.length; i++) {
      lines.push(padRight("  " + noteLines[i], LINE_WIDTH));
    }
    lines.push(blankLine);
  } else {
    lines.push(blankLine);
  }

  lines.push(leftLabel + rightLabel, blankLine, blankLine);
  return lines;
};

// ─── Public exports ──────────────────────────────────────────────────────────

/**
 * Format a full invoice as an 80-column ASCII string.
 * Matches InvoiceModal's behavior: all items across all transactions
 * are flattened into a single invoice (same as transactions.flatMap()).
 *
 * @param {Object[]} transactions - array of transaction objects
 * @param {Object}   settings     - app settings (businessName, bankAccounts, etc.)
 * @returns {string} formatted invoice text, lines joined by "\n"
 */
export const formatInvoice = (transactions, settings, options = {}, contacts = []) => {
  const txs        = Array.isArray(transactions) ? transactions : [transactions];
  const s          = settings || {};
  const { note = "" } = options;

  const lines = [
    ...formatInvoiceHeader(),
    ...formatInvoiceMeta(txs, contacts, note),
    ...formatItemsTable(txs),
    ...formatInvoiceFooter(txs, s),
  ];

  return lines.join("\n");
};

/**
 * Format a full surat jalan as an 80-column ASCII string.
 *
 * @param {Object} transaction - single transaction object
 * @param {Object} settings    - app settings (businessName, etc.)
 * @returns {string} formatted surat jalan text, lines joined by "\n"
 */
export const formatSuratJalan = (transaction, _settings, options = {}, contacts = []) => {
  const t = transaction || {};
  const { platNomor = "", catatanPengiriman = "" } = options;

  const lines = [
    ...formatSuratJalanHeader(),
    ...formatSuratJalanMeta(t, platNomor, contacts),
    ...formatSuratJalanItems(t),
    ...formatSuratJalanFooter(catatanPengiriman),
  ];

  return lines.join("\n");
};
